import { Box, Tooltip, Typography } from "@outschool/backpack";
import { Activity, Section } from "@outschool/gql-backend-generated";
import { useTranslation } from "@outschool/localization";
import { useIsMobile } from "@outschool/ui-utils";
import truncate from "lodash/truncate";
import React from "react";

import { SectionStatusChip } from "../Chips/SectionStatusChip";
import { EnrollmentWithSection } from "./ClassList";
import { CreditBox } from "./CreditBox";

interface Props {
  learnerName: string;
  leaderName: string;
  section: Section;
  activity: Activity;
  classTime: string;
  enrollment: EnrollmentWithSection;
  completed?: boolean;
}

const LEADER_NAME_DISPLAY_LIMIT = 20;

const ClassItemInfo = ({
  learnerName,
  leaderName,
  section,
  activity,
  classTime,
  enrollment,
  completed,
}: Props) => {
  const { getClassType } = useGetClassType();
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Box
        flex
        sx={{
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 8,
        }}
      >
        <Box
          flex
          sx={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: "0 1.1rem",
          }}
        >
          <SectionStatusChip size="small" section={section} />
          <Typography variant="subtitle2">{learnerName}</Typography>
        </Box>
        <Box
          flex
          sx={{
            flexDirection: "row",
            flexWrap: "wrap",
            alignItems: "baseline",
            gap: "0 1.1rem",
          }}
        >
          <Typography variant="subtitle2">{`With ${leaderName}`}</Typography>
          <GrayDot />
          <Typography variant="subtitle2">{getClassType(activity)}</Typography>
          {!completed && (
            <>
              <GrayDot />
              <CreditBox
                activity={activity}
                section={section}
                enrollment={enrollment}
              />
            </>
          )}
        </Box>
        <Typography variant="subtitle2">{classTime}</Typography>
      </Box>
    );
  }

  return (
    <Box
      flex
      sx={{
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "baseline",
        gap: "0 1.5rem",
      }}
    >
      <SectionStatusChip size="small" section={section} />
      <Typography variant="subtitle2">{learnerName}</Typography>
      <GrayDot />
      <Tooltip
        title={leaderName}
        disableHoverListener={leaderName.length <= LEADER_NAME_DISPLAY_LIMIT}
      >
        <Typography variant="subtitle2">{`With ${truncate(leaderName, {
          length: LEADER_NAME_DISPLAY_LIMIT,
        })}`}</Typography>
      </Tooltip>
      <GrayDot />
      <Typography variant="subtitle2">{getClassType(activity)}</Typography>
      {!completed && (
        <>
          <GrayDot />
          <CreditBox
            activity={activity}
            section={section}
            enrollment={enrollment}
          />
        </>
      )}
    </Box>
  );
};

const GrayDot = () => {
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: "grey.200",
      }}
    />
  );
};

const useGetClassType = () => {
  const { t } = useTranslation("client\\components\\ClassList");

  const getClassType = (activity: Activity) => {
    if (activity.isSelfPaced) {
      return t("Self paced");
    }
    if (activity.hasTeacherSchedule) {
      return t("Live 1-on-1");
    }
    if (activity.isOneTime) {
      return t("Live one-time");
    }
    if (activity.size_max == 1) {
      return t("Live private");
    }
    return t("Live group");
  };

  return { getClassType };
};

export default ClassItemInfo;
